import { io, Socket } from 'socket.io-client';

import { DocumentWebsocketStatus } from '@/enums/DocumentWebsocketStatus';
import store from '@/store/store';
import { config } from '@/utils/config';

/**
 * Obsługa websocketu odpowiedzialnego za blokowanie dokumentu
 *
 * @export
 * @class DocumentWebsocket
 */
export class DocumentWebsocket {
  private socket?: Socket;

  /**
   * Callback wywoływany przy wymuszeniu odblokowania
   *
   * @private
   * @memberof DocumentWebsocket
   */
  private onForceUnlockCallback?: () => any;

  /**
   * Zakłada blokadę
   *
   * @param {number} documentId
   * @returns {Promise<{ status: DocumentWebsocketStatus }>}
   * @memberof DocumentWebsocket
   */
  public lockDocumentEdit(documentId: number): Promise<{ status: DocumentWebsocketStatus }> {
    return new Promise((resolve, reject) => {
      this.socket = io(config.websocketURL, {
        auth: { token: store.state.auth.token },
        path: '/websockets',
        transports: ['websocket']
      });

      let connected = false;

      this.socket.on('connect_error', () => {
        if (!connected) {
          this.socket?.disconnect();
          return reject({ status: DocumentWebsocketStatus.NOT_CONNECTED });
        }
      });

      this.socket.on('connect', () => {
        connected = true;
        this.socket?.emit(
          'lockDocument',
          { documentId, token: store.state.auth.token },
          (result: { status: DocumentWebsocketStatus }) => {
            if (result.status === DocumentWebsocketStatus.OK) {
              this.setRefreshLockInterval(documentId);
              return resolve(result);
            }
            if (this.socket?.connected) {
              this.socket.disconnect();
            }
            this.socket = undefined;
            return reject(result);
          }
        );

        this.socket?.on('unlockDocument', (documentIdToUnlock: number) => {
          if (documentIdToUnlock === documentId) {
            this.onForceUnlockCallback?.();
            this.unlockDocumentEdit();
          }
        });
      });
    });
  }

  /**
   * Zdjemuje blokadę
   *
   * @memberof DocumentWebsocket
   */
  public unlockDocumentEdit(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = undefined;
      this.onForceUnlockCallback = undefined;
    }
  }

  /**
   * Ustawia callback na wymuszenie odblokowania
   *
   * @param {() => any} callback
   * @memberof DocumentWebsocket
   */
  public onForceUnlock(callback: () => any): void {
    this.onForceUnlockCallback = callback;
  }

  /**
   * Ustawia interwał odświeżający blokadę
   *
   * @private
   * @param {number} documentId
   * @memberof DocumentWebsocket
   */
  private setRefreshLockInterval(documentId: number): void {
    const intervalId = setInterval(() => {
      if (!this.socket) {
        clearInterval(intervalId);
        return;
      }
      this.socket.emit(
        'lockDocument',
        { documentId, token: store.state.auth.token },
        (result: { status: 'OK' | 'LOCKED' }) => {
          if (result.status === 'LOCKED') {
            clearInterval(intervalId);
            this.unlockDocumentEdit();
          }
        }
      );
    }, 60000);
  }
}

export const documentWebsocket = new DocumentWebsocket();
